"use client";

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useControlledState } from "./hooks";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Tabs> family. Ported
// letter-for-letter from the vendor's compiled bundle (TabsRoot/TabList/
// Tab/TabPanels/TabPanel + the roving-tabindex arrow-key navigation) and
// Tabs-DBRBCCCC.css. Real usage in this app uses BOTH the compound form
// (<Tabs.List>/<Tabs.Tab>) and the bare top-level exports (<TabList>/<Tab>
// directly) — confirmed via inventory (Tab: 6 sites/2 files, TabList: 2/2,
// TabPanel: 3/1, TabPanels: 1/1) — so both need to keep working, not just
// the compound form.
//
// Uses the local ./hooks' useControlledState (ported from
// @datasirpi/hooks, since fully removed from this app now) rather than
// reimplementing controlled/uncontrolled state merging inline here.
//
// Keyboard/ARIA behavior (arrow-key roving tabindex, Home/End, automatic
// vs. manual activation) is ported faithfully but — like Modal/Drawer —
// isn't verifiable by this repo's tooling; only a manual browser check can
// confirm it. Lower risk than Modal/Drawer, though: no portal, no focus
// trap, no scroll-lock.
// Phase 5.4: `urlParam` folds in what `components/ui/Tabs` existed to add.
// That wrapper was a second Tabs API for one feature — mirroring the active
// tab into a query string — and having two meant call sites picked whichever
// they met first, so half the tab bars in the app were deep-linkable and half
// silently were not. On a console where people paste links to each other
// ("look at the Timeline tab of incident 402"), that is a real difference,
// not a nicety. It belongs on the component everyone already uses.
export type TabsVariant = "underline" | "pill" | "segmented";
export type TabsActivation = "automatic" | "manual";

interface TabsContextValue {
  baseId: string;
  value: string | undefined;
  setValue: (next: string) => void;
  activation: TabsActivation;
  ariaLabel: string | undefined;
  registerTrigger: (value: string, node: HTMLButtonElement | null) => void;
  focusByOffset: (current: string, offset: number) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("Tabs.* must be used inside <Tabs>");
  return ctx;
}

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue"> {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  variant?: TabsVariant;
  activation?: TabsActivation;
  "aria-label"?: string;
  /* Mirrors the active tab into `?<urlParam>=<value>`, so the tab survives a
     reload and can be linked to. Uses router.replace, not push: a tab is a
     view of one page, and pushing would make Back walk through every tab the
     user glanced at instead of leaving the page. */
  urlParam?: string;
  children: ReactNode;
}

/**
 * Reads the initial tab from the URL. Pair with `urlParam` on <Tabs>:
 * this seeds the state on mount, that keeps the URL in step afterwards.
 *
 * Deliberately a plain useState, keeping `components/ui/Tabs`'s exact
 * signature — callers destructure it like any other state tuple, and
 * useControlledState's `T | undefined` value and updater-less setter would
 * have quietly broken them.
 */
export function useTabState(tabs: string[], fallback: string, urlParam = "tab") {
  const search = useSearchParams();
  const initial = search.get(urlParam);
  return useState<string>(initial && tabs.includes(initial) ? initial : fallback);
}

/* The URL sync lives in its own component, mounted only when `urlParam` is
   set, and that placement is load-bearing rather than tidiness.

   `useSearchParams()` opts a route out of static rendering in the Next App
   Router — a page that calls it without a Suspense boundary fails the build.
   Calling it directly inside TabsRoot would therefore have imposed that cost
   on all 236 <Tabs> call sites across 43 files to serve the handful that
   actually want a deep-linkable tab. Hooks cannot be called conditionally,
   but a component can be rendered conditionally, so the hook only exists on
   the pages that asked for it. */
function TabsUrlSync({ param, value }: { param: string; value: string | undefined }) {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();

  // Guarded on the comparison rather than a "has it changed" flag, so
  // landing on a URL that already names the tab navigates zero times.
  useEffect(() => {
    if (value === undefined) return;
    if (search.get(param) === value) return;
    const params = new URLSearchParams(Array.from(search.entries()));
    params.set(param, value);
    // replace, not push: a tab is a view of one page. Pushing would make
    // Back walk through every tab the user glanced at.
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    // `search` is a fresh object on every render in Next, so including it
    // would re-run this on any state change anywhere on the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, param]);

  return null;
}

const TabsRoot = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  { value, defaultValue, onChange, variant = "underline", activation = "automatic", urlParam, className, children, ...rest },
  ref
) {
  const baseId = useId();
  const [active, setActive] = useControlledState<string>({ value, defaultValue, onChange: undefined });
  const triggerRefs = useRef(new Map<string, HTMLButtonElement>());

  const registerTrigger = useCallback((v: string, node: HTMLButtonElement | null) => {
    if (node) triggerRefs.current.set(v, node);
    else triggerRefs.current.delete(v);
  }, []);

  const focusByOffset = useCallback(
    (current: string, offset: number) => {
      const values = Array.from(triggerRefs.current.keys()).filter((v) => {
        const node = triggerRefs.current.get(v);
        return node && !node.disabled;
      });
      if (values.length === 0) return;
      const idx = Math.max(0, values.indexOf(current));
      let nextIdx: number;
      if (offset >= values.length) nextIdx = values.length - 1;
      else if (offset <= -values.length) nextIdx = 0;
      else nextIdx = (((idx + offset) % values.length) + values.length) % values.length;
      const next = values[nextIdx];
      if (!next) return;
      const node = triggerRefs.current.get(next);
      node?.focus();
      if (activation === "automatic") {
        setActive(next);
        onChange?.(next);
      }
    },
    [activation, setActive, onChange]
  );

  const setValue = useCallback(
    (next: string) => {
      setActive(next);
      onChange?.(next);
    },
    [setActive, onChange]
  );

  const ariaLabel = (rest as { "aria-label"?: string })["aria-label"];
  const restWithoutLabel = { ...rest };
  delete (restWithoutLabel as { "aria-label"?: string })["aria-label"];

  const ctx = useMemo<TabsContextValue>(
    () => ({ baseId, value: active, setValue, activation, ariaLabel, registerTrigger, focusByOffset }),
    [baseId, active, setValue, activation, ariaLabel, registerTrigger, focusByOffset]
  );

  return (
    <div ref={ref} className={cn("rc-tabs", `rc-tabs--${variant}`, className)} {...restWithoutLabel}>
      {urlParam ? <TabsUrlSync param={urlParam} value={active} /> : null}
      <TabsContext.Provider value={ctx}>{children}</TabsContext.Provider>
    </div>
  );
});

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const TabList = forwardRef<HTMLDivElement, TabListProps>(function TabList({ className, children, ...rest }, ref) {
  const ctx = useTabsContext();
  const ownLabel = (rest as { "aria-label"?: string })["aria-label"];
  const label = ownLabel ?? ctx.ariaLabel;
  return (
    <div ref={ref} role="tablist" aria-label={label} className={cn("rc-tabs__list", className)} {...rest}>
      {children}
    </div>
  );
});

export interface TabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "value" | "role" | "type"> {
  value: string;
  children: ReactNode;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab({ value, disabled, className, children, onKeyDown, ...rest }, ref) {
  const ctx = useTabsContext();
  const selected = ctx.value === value;
  const tabId = `${ctx.baseId}-tab-${value}`;
  const panelId = `${ctx.baseId}-panel-${value}`;

  const setRef = useCallback(
    (node: HTMLButtonElement | null) => {
      ctx.registerTrigger(value, node);
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    },
    [ctx, value, ref]
  );

  function handleKey(e: React.KeyboardEvent<HTMLButtonElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      ctx.focusByOffset(value, 1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      ctx.focusByOffset(value, -1);
    } else if (e.key === "Home") {
      e.preventDefault();
      ctx.focusByOffset(value, -Number.MAX_SAFE_INTEGER);
    } else if (e.key === "End") {
      e.preventDefault();
      ctx.focusByOffset(value, Number.MAX_SAFE_INTEGER);
    } else if ((e.key === "Enter" || e.key === " ") && ctx.activation === "manual") {
      e.preventDefault();
      ctx.setValue(value);
    }
  }

  return (
    <button
      ref={setRef}
      type="button"
      role="tab"
      id={tabId}
      aria-selected={selected}
      aria-controls={panelId}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      className={cn("rc-tabs__trigger", className)}
      onClick={() => ctx.setValue(value)}
      onKeyDown={handleKey}
      {...rest}
    >
      {children}
    </button>
  );
});

export interface TabPanelsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const TabPanels = forwardRef<HTMLDivElement, TabPanelsProps>(function TabPanels({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={className} {...rest}>
      {children}
    </div>
  );
});

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  unmountInactive?: boolean;
  children: ReactNode;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(function TabPanel(
  { value, unmountInactive = false, className, children, ...rest },
  ref
) {
  const ctx = useTabsContext();
  const active = ctx.value === value;
  const tabId = `${ctx.baseId}-tab-${value}`;
  const panelId = `${ctx.baseId}-panel-${value}`;
  if (unmountInactive && !active) return null;
  return (
    <div
      ref={ref}
      role="tabpanel"
      id={panelId}
      aria-labelledby={tabId}
      hidden={!active}
      tabIndex={0}
      className={cn("rc-tabs__panel", className)}
      {...rest}
    >
      {children}
    </div>
  );
});

type TabsCompound = typeof TabsRoot & {
  List: typeof TabList;
  Tab: typeof Tab;
  Panels: typeof TabPanels;
  Panel: typeof TabPanel;
};

const Tabs = TabsRoot as TabsCompound;
Tabs.List = TabList;
Tabs.Tab = Tab;
Tabs.Panels = TabPanels;
Tabs.Panel = TabPanel;

export { Tabs };
